import { Button, DialogRoot, DialogTrigger } from '@ibsys/kit';
import { IconButton } from '@/shared/ui';
import { XCircleIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UserGroupListItem, useUserGroups } from '@/entities/userGroups';
import { NestedItemCard } from '@/features/nested-items';
import { UserFormValues } from '../UserForm/types';
import { AddUserGroupsDialog } from './AddUserGroupsDialog';

type Props = {
  disabled?: boolean;
};

export const UserGroupsList: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation(['users', 'common']);
  const { setValue, getValues, watch } = useFormContext<UserFormValues>();
  const fields = watch('userGroups');

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Запрос для получения полных данных всех групп пользователей
  const { data: groupsData } = useUserGroups({ limit: 100 });
  const allGroups = groupsData?.userGroups ?? [];

  const currentGroups = watch('userGroups');

  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleCancelDialog = () => setIsDialogOpen(false);

  const handleSaveDialog = (newGroups: UserGroupListItem[]) => {
    // Сохраняем только минимальные данные (id)
    const newGroupIds = newGroups.map((group) => ({ id: group.id }));
    setValue('userGroups', [...(getValues('userGroups') || []), ...newGroupIds]);
    setIsDialogOpen(false);
  };

  const handleRemoveGroup = (id: number) => {
    setValue('userGroups', getValues('userGroups')?.filter((group) => group.id !== id) || []);
  };

  // Поиск полных данных по идентификатору
  const getGroupData = (id: number): UserGroupListItem | undefined => {
    return allGroups.find((group) => group.id === id);
  };

  return (
    <DialogRoot open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <div className="flex flex-col gap-4">
        <div className="flex gap-5 items-center">
          <p className="font-medium">{t('group')}</p>

          {!disabled && (
            <DialogTrigger asChild>
              <Button variant="secondary" size="sm" className="ml-auto" onClick={handleOpenDialog}>
                {t('addGroup')}
              </Button>
            </DialogTrigger>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {fields.map((field) => {
            const groupData = getGroupData(field.id);
            if (!groupData) return null;
            return (
              <NestedItemCard
                key={groupData.id}
                item={groupData}
                renderTitle={(item) => item.name}
                action={
                  !disabled && (
                    <IconButton
                      icon={<XCircleIcon />}
                      variant="ghost"
                      color="destructive"
                      label={t('users:removeGroupFromUser')}
                      onClick={() => handleRemoveGroup(field.id)}
                    />
                  )
                }
              />
            );
          })}
          {fields.length === 0 && <p className="text-sm text-muted-foreground">{t('noGroupsInUser')}</p>}
        </div>
      </div>

      <AddUserGroupsDialog
        existingGroupIds={currentGroups?.map((group) => group.id) || []}
        onCancel={handleCancelDialog}
        onSave={handleSaveDialog}
        className="max-w-160"
      />
    </DialogRoot>
  );
};
